import { useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, Save } from 'lucide-react';
import { useEffect, useId, useRef, useState, type MouseEvent, type ReactNode } from 'react';
import {
  MESSAGE_BODY_MAX,
  MESSAGE_SUBJECT_MAX,
  MESSAGE_VARIABLES,
  fromLabelledText,
  labelledTokens,
  templateAudienceOf,
  toLabelledText,
  variableLabel,
  type TemplateAudience,
  type TemplateRow,
  type TemplateVersionRow,
} from '@nest-recruit/shared';
import { Button } from '../../../../components/Button';
import { Field } from '../../../../components/Field';
import { Input } from '../../../../components/Input';
import { Segmented } from '../../../../components/Segmented';
import { ApiError, errorMessage } from '../../../../lib/api-client';
import { formatDate, formatNumber } from '../../../../lib/format';
import { useToasts } from '../../../../stores/toasts';
import { TEMPLATES_KEY, communicationApi } from '../../api/communication-api';
import { PillTextField } from './PillTextField';
import { TemplatePreviewPane, type PreviewChoice, type PreviewDevice } from './TemplatePreviewPane';
import { useTemplateDraft, type TemplateDraft } from './use-template-draft';
import styles from './TemplateWorkspace.module.css';

export const ADMIN_TEMPLATES_KEY = [...TEMPLATES_KEY, 'admin'] as const;
const NAME_MIN = 2;
const NAME_MAX = 120;

export type WorkspaceLayout = 'split' | 'tabs' | 'page';

/**
 * ADMIN-09's middle and right panes (Option A): the wording with variables as pills, and the email it makes. A save is
 * a new version and changes only what is sent from now on. The team's own can be switched off, with an Undo.
 *
 * `split` is the three-pane width; `tabs` puts Write and Preview behind a switch (1180 px and below); `page` is the
 * phone's own screen, with the way back to the list.
 */
export function TemplateWorkspace({
  template,
  viewing,
  layout,
  choice,
  device,
  onChoice,
  onDevice,
  onCloseVersion,
  onDirtyChange,
  onSaved,
  onBack,
}: {
  /** The template as the server has it now; null for a new one of the team's. */
  readonly template: TemplateRow | null;
  readonly viewing: TemplateVersionRow | null;
  readonly layout: WorkspaceLayout;
  readonly choice: PreviewChoice | null;
  readonly device: PreviewDevice;
  readonly onChoice: (choice: PreviewChoice | null) => void;
  readonly onDevice: (device: PreviewDevice) => void;
  readonly onCloseVersion: () => void;
  readonly onDirtyChange: (isDirty: boolean) => void;
  readonly onSaved: (row: TemplateRow) => void;
  /** The phone's way back to the list; the page asks first if the words are unsaved. */
  readonly onBack: (event: MouseEvent<HTMLButtonElement>) => void;
}): ReactNode {
  const queryClient = useQueryClient();
  const show = useToasts((state) => state.show);
  const showUndo = useToasts((state) => state.showUndo);
  const state = useTemplateDraft(template);
  const { draft, base, isDirty, theirs } = state;
  const [tab, setTab] = useState<'write' | 'preview'>('write');
  const [isSaving, setIsSaving] = useState(false);
  const [isSwitching, setIsSwitching] = useState(false);
  const subjectRef = useRef<HTMLTextAreaElement | null>(null);
  const bodyRef = useRef<HTMLTextAreaElement | null>(null);
  const lastField = useRef<'subject' | 'body'>('body');
  const paletteId = useId();

  useEffect(() => onDirtyChange(isDirty), [isDirty, onDirtyChange]);

  const isSystem = template !== null && template.kind === 'system';
  const palette: readonly string[] = template?.placeholders ?? MESSAGE_VARIABLES;
  const audience: TemplateAudience = template && isSystem ? templateAudienceOf(template) : 'candidates';
  // What is on screen: an earlier version being read, or the draft.
  const shown: TemplateDraft = viewing ? { name: draft.name, subject: toLabelledText(viewing.subject), body: toLabelledText(viewing.bodyText ?? '') } : draft;
  const subjectKeys = viewing ? viewing.subject : fromLabelledText(draft.subject, palette);
  const bodyKeys = viewing ? (viewing.bodyText ?? '') : fromLabelledText(draft.body, palette);
  const cannotFill = [...labelledTokens(draft.subject, palette), ...labelledTokens(draft.body, palette)].filter((token) => !token.isKnown).map((token) => token.text);
  const nameError = draft.name.trim().length < NAME_MIN ? 'Name the template: at least 2 characters.' : undefined;
  const canSave = isDirty && !theirs && !viewing && !nameError && draft.subject.trim() !== '' && draft.body.trim() !== '' && cannotFill.length === 0;

  const insert = (name: string): void => {
    const isSubject = lastField.current === 'subject';
    const field = isSubject ? subjectRef.current : bodyRef.current;
    const current = isSubject ? draft.subject : draft.body;
    const token = `{{${variableLabel(name)}}}`;
    const start = field?.selectionStart ?? current.length;
    const end = field?.selectionEnd ?? current.length;
    state.setField(isSubject ? 'subject' : 'body', `${current.slice(0, start)}${token}${current.slice(end)}`);
    // The cursor goes after the pill, once React has drawn the new text.
    requestAnimationFrame(() => {
      field?.focus();
      field?.setSelectionRange(start + token.length, start + token.length);
    });
  };

  const save = async (): Promise<void> => {
    if (!canSave) return;
    setIsSaving(true);
    try {
      const subject = fromLabelledText(draft.subject.trim(), palette);
      const bodyText = fromLabelledText(draft.body.trim(), palette);
      const saved = base
        ? await communicationApi.saveTemplate(base.id, { name: draft.name.trim(), description: base.description, subject, bodyText, version: base.version })
        : await communicationApi.createTemplate({ name: draft.name.trim(), description: null, subject, bodyText });
      // The list shows the new version at once; the refetch confirms it.
      queryClient.setQueryData<readonly TemplateRow[]>(ADMIN_TEMPLATES_KEY, (rows) => (rows ? (rows.some((row) => row.id === saved.id) ? rows.map((row) => (row.id === saved.id ? saved : row)) : [...rows, saved]) : rows));
      state.adopt(saved);
      show({ tone: 'success', message: base ? `Saved as version ${saved.version}` : `${saved.name} created`, detail: 'It applies to emails sent from now on.' });
      onSaved(saved);
      void queryClient.invalidateQueries({ queryKey: TEMPLATES_KEY });
    } catch (cause) {
      const isStale = cause instanceof ApiError && cause.status === 409;
      show({ tone: 'danger', message: "The template wasn't saved", detail: isStale ? 'Someone else saved it first. Your words are still here: take their version to keep your changes on top of it.' : errorMessage(cause) });
      if (isStale) void queryClient.invalidateQueries({ queryKey: ADMIN_TEMPLATES_KEY });
    } finally {
      setIsSaving(false);
    }
  };

  /** Act first, with an Undo (06-qol §4). The version is read from the cache when the click lands (review W16). */
  const switchTo = async (isOn: boolean, isUndo = false): Promise<void> => {
    if (!template) return;
    const cached = queryClient.getQueryData<readonly TemplateRow[]>(ADMIN_TEMPLATES_KEY)?.find((row) => row.id === template.id) ?? template;
    const before = queryClient.getQueryData<readonly TemplateRow[]>(ADMIN_TEMPLATES_KEY);
    queryClient.setQueryData<readonly TemplateRow[]>(ADMIN_TEMPLATES_KEY, (rows) => rows?.map((row) => (row.id === template.id ? { ...row, isActive: isOn } : row)));
    setIsSwitching(true);
    try {
      const row = await communicationApi.switchTemplate(template.id, cached.version, isOn);
      queryClient.setQueryData<readonly TemplateRow[]>(ADMIN_TEMPLATES_KEY, (rows) => rows?.map((each) => (each.id === row.id ? row : each)));
      if (!isUndo) showUndo(isOn ? `${row.name} switched on` : `${row.name} switched off`, () => void switchTo(!isOn, true));
    } catch (cause) {
      queryClient.setQueryData(ADMIN_TEMPLATES_KEY, before);
      show({ tone: 'danger', message: isOn ? "It wasn't switched on" : "It wasn't switched off", detail: errorMessage(cause) });
    } finally {
      setIsSwitching(false);
      await queryClient.invalidateQueries({ queryKey: ADMIN_TEMPLATES_KEY });
    }
  };

  const editor = (
    <form
      className={styles.editor}
      aria-label={template ? `Edit ${template.name}` : 'New template'}
      onSubmit={(event) => {
        event.preventDefault();
        void save();
      }}
      onKeyDown={(event) => {
        if (event.key === 'Enter' && (event.ctrlKey || event.metaKey)) {
          event.preventDefault();
          void save();
        }
      }}
    >
      <header className={styles.head}>
        {layout === 'page' && (
          <Button variant="ghost" size="sm" icon={ArrowLeft} onClick={(event) => onBack(event)}>
            Message templates
          </Button>
        )}
        <div className={styles.titleRow}>
          <h2 className={styles.title}>{template ? template.name : 'New template'}</h2>
          <span className={styles.chip}>{template ? `${isSystem ? 'Sent by the system' : "The team's own"} · version ${template.version}` : 'For messages you write to candidates'}</span>
        </div>
      </header>

      {theirs && (
        <div className={styles.notice} role="status">
          <span>
            Someone else saved version {theirs.version} while you were editing. Your changes are kept here; take their version to save on top of it.
          </span>
          <Button variant="quiet" size="sm" onClick={state.takeTheirs}>
            Take their version, keep my changes
          </Button>
        </div>
      )}
      {viewing && (
        <div className={styles.notice} role="status">
          <span>
            Version {viewing.version} · {formatDate(viewing.createdAt)} · {viewing.createdByName ?? 'The system'}. This is how it read then; nothing changes until you save.
          </span>
          <span className={styles.noticeActions}>
            <Button
              variant="quiet"
              size="sm"
              onClick={() => {
                state.useWording(viewing);
                onCloseVersion();
              }}
            >
              Use this wording
            </Button>
            <Button variant="ghost" size="sm" onClick={onCloseVersion}>
              {isDirty ? 'Back to my draft' : 'Back to the current wording'}
            </Button>
          </span>
        </div>
      )}

      <div className={styles.fields}>
        <Field label="Name" isRequired error={!viewing && draft.name !== '' ? nameError : undefined}>
          {({ inputId, describedBy, isInvalid }) => <Input id={inputId} aria-describedby={describedBy} isInvalid={isInvalid} value={shown.name} maxLength={NAME_MAX} readOnly={viewing !== null} onChange={(event) => state.setField('name', event.target.value)} />}
        </Field>
        <Field label="Subject" isRequired>
          {({ inputId, describedBy }) => (
            <PillTextField
              ref={subjectRef}
              id={inputId}
              describedBy={describedBy}
              value={viewing ? shown.subject : draft.subject}
              palette={palette}
              isSingleLine
              isReadOnly={viewing !== null}
              maxLength={MESSAGE_SUBJECT_MAX}
              onFocus={() => {
                lastField.current = 'subject';
              }}
              onChange={(value) => state.setField('subject', value)}
            />
          )}
        </Field>
        <Field
          label="Message"
          isRequired
          hint={`Blank lines separate paragraphs. Click a variable to put it where the cursor is.${palette.includes('deepLinkUrl') ? ' A line with Button link becomes the email’s button.' : ''}`}
          {...(cannotFill.length > 0 && !viewing ? { error: `This email can’t fill ${[...new Set(cannotFill)].join(', ')}. Use the variable buttons.` } : {})}
        >
          {({ inputId, describedBy, isInvalid }) => (
            <PillTextField
              ref={bodyRef}
              id={inputId}
              describedBy={describedBy}
              value={viewing ? shown.body : draft.body}
              palette={palette}
              isInvalid={isInvalid}
              isReadOnly={viewing !== null}
              maxLength={MESSAGE_BODY_MAX}
              onFocus={() => {
                lastField.current = 'body';
              }}
              onChange={(value) => state.setField('body', value)}
            />
          )}
        </Field>
        {!viewing && (
          <div className={styles.palette}>
            <span className={styles.paletteLabel} id={paletteId}>
              {isSystem ? 'This email can use' : 'Variables'}
            </span>
            <div className={styles.pills} role="group" aria-labelledby={paletteId}>
              {palette.map((name) => (
                <button key={name} type="button" className={styles.pillButton} onClick={() => insert(name)}>
                  {variableLabel(name)}
                </button>
              ))}
            </div>
          </div>
        )}
      </div>

      <footer className={styles.bar}>
        <span className={styles.barText}>{template ? `Used ${formatNumber(template.usage.total)} ${template.usage.total === 1 ? 'time' : 'times'} · ${formatNumber(template.usage.last30Days)} in 30 days. Applies to emails sent from now on.` : 'Applies to emails sent from now on.'}</span>
        {template && !isSystem && (
          <Button variant="quiet" size="sm" isPending={isSwitching} onClick={() => void switchTo(!template.isActive)}>
            {template.isActive ? 'Switch off' : 'Switch on'}
          </Button>
        )}
        <Button variant="solid" type="submit" icon={Save} isPending={isSaving} disabled={!canSave}>
          {template ? 'Save new version' : 'Create template'}
        </Button>
      </footer>
    </form>
  );

  const preview = <TemplatePreviewPane templateId={template?.id ?? null} audience={audience} subject={subjectKeys} bodyText={bodyKeys} choice={choice} device={device} onChoice={onChoice} onDevice={onDevice} />;

  if (layout === 'split') {
    return (
      <>
        {editor}
        {preview}
      </>
    );
  }
  return (
    <div className={styles.stack}>
      <Segmented
        label="Show"
        value={tab}
        options={[
          { value: 'write', label: 'Write' },
          { value: 'preview', label: 'Preview' },
        ]}
        onChange={setTab}
      />
      {/* Both stay mounted, so switching back keeps the cursor and the drawn email. */}
      <div hidden={tab !== 'write'}>{editor}</div>
      <div hidden={tab !== 'preview'}>{preview}</div>
    </div>
  );
}
